// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createActionsHook,
	createHook,
	type Action,
	type BoundActions,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import type { DiffItem, OnSearchCallback } from '../../common/types.tsx';
import { nonReactiveSingleton } from '../../common/non-reactive-store.tsx';

type ModalDismissSource =
	| 'CROSS_CLOSE_BUTTON'
	| 'ACCEPT_FIX'
	| 'ON_ENTER_NL_MODE'
	| 'NETWORK_CALL_ABORT'
	| 'ON_NEW_JQL_SEARCH';

export type State = {
	jqlQuery: string;
	userQueryError: string[];
	onSearch: NonNullable<OnSearchCallback>;
	status:
		| 'INIT'
		| 'LOADING_RESULT'
		| 'VIEW_RESULT'
		| 'RESULT_ERROR'
		| 'RESULT_DISCARDED' // TODO - remove this status with the fg cleanup of jql_fixer_analytics_fixes
		| 'JS_ERROR'
		| 'UNRECOVERABLE_ERROR';
	jqlDiffTokens: DiffItem[];
	isAiEnabled: boolean;
	syntaxCorrectionAttemptErrors: string[];
	singleInstrumentationID: string;
	onAiInteractionDismissed: (source: ModalDismissSource) => void;
	onJsError: () => void;
	exitNaturalLanguageMode: () => void;
	errorSectionTestId: string;
	jqlErrors: string[];
	errorComponentValidationId?: string;
	errorComponentTestId: string;
	// Currently using the undefined state to cover both NIN basic mode and NIN advanced mode
	issueNavigatorSearchMode?: 'NL2JQL';
	isReadyForProactive: boolean;
};

export type Actions = {
	setJqlQuery: (query: State['jqlQuery']) => Action<State>;
	setOnSearch: (status: State['onSearch']) => Action<State>;
	setStatus: (status: State['status']) => Action<State>;
	resetStatus: (source: ModalDismissSource, newJqlQuery?: string) => Action<State>;
	setIsAiEnabled: (isAiEnabled: boolean) => Action<State>;
	setStateOnFeatureInvoke: (
		data: Pick<State, 'jqlQuery' | 'singleInstrumentationID' | 'errorSectionTestId'>,
	) => Action<State>;
	setJqlErorCorrectionData: (
		data: Pick<
			State,
			'jqlDiffTokens' | 'userQueryError' | 'onSearch' | 'syntaxCorrectionAttemptErrors'
		>,
	) => Action<State>;
	setOnAiInteractionDismissed: (callback: State['onAiInteractionDismissed']) => Action<State>;
	setOnJsError: (callback: State['onJsError']) => Action<State>;
	exitNaturalLanguageMode: () => Action<State>;
	setExitNaturalLanguageMode: (callback: State['exitNaturalLanguageMode']) => Action<State>;
	setJqlErrors: (callback: State['jqlErrors']) => Action<State>;
	setErrorComponentValidationId: (callback: State['errorComponentValidationId']) => Action<State>;
	setErrorComponentTestId: (callback: State['errorComponentTestId']) => Action<State>;
	setIsReadyForProactive: (isReadyForProactive: State['isReadyForProactive']) => Action<State>;
	setIssueNavigatorSearchMode: (
		issueNavigatorSearchMode: State['issueNavigatorSearchMode'],
	) => Action<State>;
};

const initialState: State = {
	jqlQuery: '',
	userQueryError: [],
	onSearch: () => undefined,
	status: 'INIT',
	jqlDiffTokens: [],
	isAiEnabled: false,
	syntaxCorrectionAttemptErrors: [],
	singleInstrumentationID: '',
	errorSectionTestId: '',
	onAiInteractionDismissed() {
		// do nothing
	},
	exitNaturalLanguageMode() {
		// do nothing
	},
	onJsError() {
		// do nothing
	},
	jqlErrors: [],
	errorComponentValidationId: '',
	errorComponentTestId: '',
	isReadyForProactive: false,
};

const Store = createStore<State, Actions>({
	initialState,
	// actions that trigger store mutation
	actions: {
		setJqlQuery:
			(jqlQuery) =>
			({ setState }) => {
				setState({ jqlQuery });
			},
		setOnSearch:
			(onSearch) =>
			({ setState }) => {
				setState({ onSearch });
			},
		setStatus:
			(status) =>
			({ setState, getState }) => {
				if (status === 'JS_ERROR') {
					getState().onJsError();
				}
				setState({ status });
			},
		setIssueNavigatorSearchMode:
			(issueNavigatorSearchMode) =>
			({ setState }) => {
				setState({ issueNavigatorSearchMode });
			},
		resetStatus:
			(source: ModalDismissSource, newJqlQuery?: string) =>
			({ setState, getState }) => {
				if (source === 'ON_ENTER_NL_MODE') {
					setState({ issueNavigatorSearchMode: 'NL2JQL' });
				}

				if (getState().status !== 'INIT') {
					if (source !== 'ACCEPT_FIX') {
						// AI modal gets dismissed when we accept a suggestion
						// However, we don't want to fire the `aiInteraction Dismissed` event in that case
						// On a new JQL search, we do not dismiss the AI interaction if the JQL query hasn't changed
						if (!(source === 'ON_NEW_JQL_SEARCH' && getState().jqlQuery === newJqlQuery)) {
							getState().onAiInteractionDismissed(source);
						}
					}

					// Reset the status and aiInteractionDismissed handler so that duplicate events won't be fired
					setState({
						status: 'INIT',
						onAiInteractionDismissed: initialState.onAiInteractionDismissed,
					});

					if (fg('jql_fixer_analytics_fixes')) {
						nonReactiveSingleton().renewSingleInstrumentationId();
					}
				}
			},
		setStateOnFeatureInvoke:
			(initialDataOnInvoke) =>
			({ setState }) => {
				setState({
					...initialDataOnInvoke,
					status: 'LOADING_RESULT',
				});
			},
		setJqlErorCorrectionData:
			(data) =>
			({ setState }) => {
				setState({
					...data,
					status: 'VIEW_RESULT',
				});
			},
		setIsAiEnabled:
			(isAiEnabled) =>
			({ setState }) => {
				setState({ isAiEnabled });
			},
		setOnAiInteractionDismissed:
			(onAiInteractionDismissed) =>
			({ setState }) => {
				setState({ onAiInteractionDismissed });
			},
		setOnJsError:
			(onJsError) =>
			({ setState }) => {
				setState({ onJsError });
			},
		exitNaturalLanguageMode:
			() =>
			({ getState }) => {
				getState().exitNaturalLanguageMode?.();
			},
		setExitNaturalLanguageMode:
			(exitNaturalLanguageMode) =>
			({ setState }) => {
				setState({ exitNaturalLanguageMode });
			},
		setJqlErrors:
			(jqlErrors) =>
			({ setState }) =>
				setState({ jqlErrors }),
		setErrorComponentValidationId:
			(errorComponentValidationId) =>
			({ setState }) =>
				setState({ errorComponentValidationId }),
		setErrorComponentTestId:
			(errorComponentValidationId) =>
			({ setState }) =>
				setState({ errorComponentValidationId }),
		setIsReadyForProactive:
			(isReadyForProactive) =>
			({ setState }) => {
				setState({ isReadyForProactive });
			},
	},
});

export const useJqlDebuggerStoreActions = createActionsHook(Store);

export const useStoreData = createHook(Store);

export const useDebuggerStatus = createHook(Store, {
	selector: (state) => state.status,
});

export const useJqlErrors = createHook(Store, {
	selector: (state) => state.jqlErrors,
});

export const useJqlEditorOnSearch = createHook(Store, {
	selector: (state) => state.onSearch,
});

export const useJqlQuery = createHook(Store, {
	selector: (state) => state.jqlQuery,
});

export const useIsAiEnabled = createHook(Store, {
	selector: (state) => state.isAiEnabled,
});

export const useErrorComponentTestId = createHook(Store, {
	selector: (state) => state.errorComponentTestId,
});

export const useErrorComponentValidationId = createHook(Store, {
	selector: (state) => state.errorComponentValidationId,
});

export const useIsReadyForProactive = createHook(Store, {
	selector: (state) => state.isReadyForProactive && state.issueNavigatorSearchMode !== 'NL2JQL',
});

export const useSyntaxCorrectionAttemptErrors = createHook(Store, {
	selector: (state) => state.syntaxCorrectionAttemptErrors,
});

export type StoreActionsShape = BoundActions<State, Actions>;
